import React, { Fragment, useMemo } from 'react';

import type { Target } from '@rtkwlf/rendall';
import { SourceType } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { Box, Chip, Tooltip } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';

type ScanTargetsProps = {
  targets: Array<Target>;
  source: SourceType;
  truncate?: boolean;
};

const removeEmptyTargets = (targets: Target[]) =>
  targets.filter((t) => t.name || t.accountID || t.cidrBlock || t.ipAddress);

export const ScanTargets: React.FC<ScanTargetsProps> = ({
  targets,
  source,
  truncate = true,
}) => {
  const overflowLimit = 30;
  const filteredTargets = useMemo(() => removeEmptyTargets(targets), [targets]);
  const { t } = useTranslation('telemetryScanSchedules');

  const targetsFields = useMemo(() => {
    let targetsLength = 0;

    const viewableTargets: string[] = [];
    const targetsForTooltip: string[] = [];

    filteredTargets.forEach((t, index) => {
      let targetDisplayName = '';

      if (source === SourceType.Iva || source === undefined) {
        targetDisplayName = t.ipAddress ?? '';
      } else if (source === SourceType.Agent) {
        targetDisplayName = t.name || t.ipAddress || '';
      } else if (source === SourceType.Eva) {
        targetDisplayName =
          t.name || t.accountID || t.cidrBlock || t.ipAddress || '';
      }

      if (targetDisplayName) {
        targetsLength += targetDisplayName.length;

        if (index === 0 || targetsLength < overflowLimit || !truncate) {
          viewableTargets.push(targetDisplayName);
        }

        targetsForTooltip.push(targetDisplayName);
      }
    });

    const filteredViewable = Array.from(new Set(viewableTargets));
    const filteredTooltip = Array.from(new Set(targetsForTooltip));

    return (
      <Stack
        direction="row"
        gap={0.5}
        flexWrap="wrap"
        data-testid="scan-targets"
      >
        {filteredViewable.map((tName: string, index: number) => (
          <Fragment key={index}>
            <Chip label={tName} color="default" variant="filled" />
          </Fragment>
        ))}

        {truncate && filteredTargets.length > viewableTargets.length ? (
          <Tooltip
            title={filteredTooltip.map((tName) => tName).join(', ')}
            placement="top"
          >
            <Chip label="..." variant="filled" color="default" />
          </Tooltip>
        ) : (
          ''
        )}
      </Stack>
    );
  }, [source, filteredTargets, truncate]);

  if (filteredTargets.length === 0) {
    return t('scanTargets.noTargets');
  }

  return (
    <Box color={(theme) => theme.palette.primary.main} whiteSpace="nowrap">
      {targetsFields}
    </Box>
  );
};
